export const PersonStatus = {
  Living: "living",
  PassedAway: "passed-away",
  NotSure: "not-sure",
} as const;

export type PersonStatusValue = (typeof PersonStatus)[keyof typeof PersonStatus];

export const PERSON_STATUS_OPTIONS: {
  value: PersonStatusValue;
  label: string;
  description?: string;
}[] = [
  { value: PersonStatus.Living, label: "Living" },
  {
    value: PersonStatus.PassedAway,
    label: "Passed away",
    description: "The court asks for a few extra details, and you'll be guided through each one.",
  },
  {
    value: PersonStatus.NotSure,
    label: "Not sure",
    description: "Many families start here, and you can update this whenever you learn more.",
  },
];

export const AdditionalDetail = {
  Minor: "minor",
  ActiveMilitary: "active-military",
  Veteran: "veteran",
  OutsideUS: "outside-us",
  NotUSCitizen: "not-us-citizen",
  Estranged: "estranged",
  CantBeLocated: "cant-be-located",
  InPrison: "in-prison",
  Incapacitated: "incapacitated",
  HasGuardian: "has-guardian",
  NoneApply: "none-apply",
} as const;

export type AdditionalDetailValue = (typeof AdditionalDetail)[keyof typeof AdditionalDetail];

export const ADDITIONAL_DETAIL_OPTIONS: {
  value: AdditionalDetailValue;
  label: string;
  description?: string;
}[] = [
  {
    value: AdditionalDetail.Minor,
    label: "Minor (under 18)",
    description:
      "Courts treat minors differently, especially around signatures. Anything a minor can't sign or receive goes to the parent or guardian instead.",
  },
  {
    value: AdditionalDetail.ActiveMilitary,
    label: "On active military duty",
    description:
      "Courts give service members extra time and protections, so this can affect deadlines.",
  },
  {
    value: AdditionalDetail.Veteran,
    label: "Veteran",
    description: "Veteran status can open benefits owed to the family, so it's worth noting.",
  },
  {
    value: AdditionalDetail.OutsideUS,
    label: "Resides outside of US",
    description:
      "Court papers may need to travel internationally, which changes how notice is delivered and how long it takes.",
  },
  {
    value: AdditionalDetail.NotUSCitizen,
    label: "Not a US citizen",
    description:
      "The court asks about citizenship on its forms, but it doesn't change what anyone inherits.",
  },
  {
    value: AdditionalDetail.Estranged,
    label: "Estranged",
    description:
      "This person stays on the court's list and receives formal notice, and there's help with how that happens. Elayne will never reach out without your permission.",
  },
  {
    value: AdditionalDetail.CantBeLocated,
    label: "Can't be located",
    description:
      "The court asks what efforts were made to find someone. Noting this early will help keep a record of what's been tried.",
  },
  {
    value: AdditionalDetail.InPrison,
    label: "In prison",
    description:
      "Courts have a specific process for delivering notice to a facility, so knowing this avoids delays.",
  },
  {
    value: AdditionalDetail.Incapacitated,
    label: "Incapacitated without a guardian (over 18)",
    description:
      "The court appoints someone to protect this person's interest in the estate. This is routine, and nothing is lost.",
  },
  {
    value: AdditionalDetail.HasGuardian,
    label: "Has a guardian or conservator (over 18)",
    description: "The guardian signs and receives court papers on this person's behalf.",
  },
  {
    value: AdditionalDetail.NoneApply,
    label: "None apply",
  },
];

export const ProfessionalRole = {
  EstateAttorney: "estate-attorney",
  OtherAttorney: "other-attorney",
  AccountantTaxAdvisor: "accountant-tax-advisor",
  FinancialAdvisor: "financial-advisor",
  InsuranceAgent: "insurance-agent",
  RealEstateAgent: "real-estate-agent",
  FuneralDirector: "funeral-director",
  FiduciaryTrustee: "fiduciary-trustee",
  CaregiverSocialWorker: "caregiver-social-worker",
  OtherProfessional: "other-professional",
} as const;

export type ProfessionalRoleValue = (typeof ProfessionalRole)[keyof typeof ProfessionalRole];

export const PROFESSIONAL_ROLE_OPTIONS: {
  value: ProfessionalRoleValue;
  label: string;
  description?: string;
}[] = [
  {
    value: ProfessionalRole.EstateAttorney,
    label: "Estate attorney",
    description:
      "Handles the estate's court process: filings, notices, and questions about authority.",
  },
  {
    value: ProfessionalRole.OtherAttorney,
    label: "Other attorney",
    description:
      "Works on legal matters outside the estate itself, like a lawsuit, a business, or real estate.",
  },
  {
    value: ProfessionalRole.AccountantTaxAdvisor,
    label: "Accountant or tax advisor",
    description:
      "Prepares tax returns for the estate or the person who passed, or advises on what's owed.",
  },
  {
    value: ProfessionalRole.FinancialAdvisor,
    label: "Financial advisor",
    description:
      "Managed investments or retirement accounts for the person who passed, or advises the estate now.",
  },
  {
    value: ProfessionalRole.InsuranceAgent,
    label: "Insurance agent",
    description: "Handles life insurance or other policies connected to the estate.",
  },
  {
    value: ProfessionalRole.RealEstateAgent,
    label: "Real estate agent",
    description: "Helps sell or manage property the estate owns.",
  },
  {
    value: ProfessionalRole.FuneralDirector,
    label: "Funeral director",
    description: "Handled the funeral arrangements. Often the source of death certificates.",
  },
  {
    value: ProfessionalRole.FiduciaryTrustee,
    label: "Fiduciary or trustee",
    description:
      "A professional paid to manage the estate or a trust, rather than a family member serving in the role.",
  },
  {
    value: ProfessionalRole.CaregiverSocialWorker,
    label: "Caregiver or social worker",
    description:
      "Cared for or supported the person who passed. May know about accounts, property, or wishes.",
  },
  {
    value: ProfessionalRole.OtherProfessional,
    label: "Other professional",
    description: "Works with the estate in a way not listed here. You can add detail in notes.",
  },
];

export const EstateRole = {
  Petitioner: "petitioner",
  NominatedExecutor: "nominated-executor",
  Executor: "executor",
  SuccessorExecutor: "successor-executor",
  Administrator: "administrator",
  VoluntaryAdministrator: "voluntary-administrator",
  Beneficiary: "beneficiary",
  Heir: "heir",
  Trustee: "trustee",
  SuccessorTrustee: "successor-trustee",
  Guardian: "guardian",
  SuccessorGuardian: "successor-guardian",
  WillWitness: "will-witness",
  NotSure: "not-sure",
} as const;

export type EstateRoleValue = (typeof EstateRole)[keyof typeof EstateRole];

export const ESTATE_ROLE_OPTIONS: {
  value: EstateRoleValue;
  label: string;
  description?: string;
}[] = [
  {
    value: EstateRole.Petitioner,
    label: "Petitioner",
    description: "The person who filed with the court to be granted authority over the estate.",
  },
  {
    value: EstateRole.NominatedExecutor,
    label: "Nominated executor",
    description: "Named in the will to settle the estate, but not yet confirmed by the court.",
  },
  {
    value: EstateRole.Executor,
    label: "Executor",
    description: "Confirmed by the court to settle the estate the will describes.",
  },
  {
    value: EstateRole.SuccessorExecutor,
    label: "Successor executor",
    description: "Named in the will as the backup, stepping in if the first executor can't serve.",
  },
  {
    value: EstateRole.Administrator,
    label: "Administrator",
    description:
      "Appointed by the court to settle the estate when there's no will, or no executor able to serve.",
  },
  {
    value: EstateRole.VoluntaryAdministrator,
    label: "Voluntary administrator",
    description:
      "Settles a small estate through a simpler court process, with more limited powers than an administrator.",
  },
  {
    value: EstateRole.Beneficiary,
    label: "Beneficiary",
    description: "Named in the will to receive part of the estate.",
  },
  {
    value: EstateRole.Heir,
    label: "Heir",
    description: "Entitled by law to receive part of the estate when there's no will.",
  },
  {
    value: EstateRole.Trustee,
    label: "Trustee",
    description: "Manages a trust and its property for the people the trust names.",
  },
  {
    value: EstateRole.SuccessorTrustee,
    label: "Successor trustee",
    description: "Named in the trust as the backup, stepping in if the first trustee can't serve.",
  },
  {
    value: EstateRole.Guardian,
    label: "Guardian",
    description:
      "Appointed to care for a minor or for an adult who can't manage their own affairs.",
  },
  {
    value: EstateRole.SuccessorGuardian,
    label: "Successor guardian",
    description: "Named as the backup, stepping in if the first guardian can't serve.",
  },
  {
    value: EstateRole.WillWitness,
    label: "Will witness",
    description:
      "Watched the will being signed. If the will doesn't include a self-proving affidavit, the court needs a short written statement from each witness.",
  },
  {
    value: EstateRole.NotSure,
    label: "Not sure",
    description:
      "Roles often become clear as the estate moves forward, and you can update this whenever you learn more.",
  },
];
